import * as React from "react";
import {Link, Typography} from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitleCommon, {
  DialogCloseCommon,
} from "components/Dialogs/DialogTitleCommon";
import EdgeIcon from "../../assets/Edge.png";
import SafariIcon from "../../assets/Safari.png";
import {useAppSelector} from "../../redux/store";
import { CPSConstants } from "commonUtils/enumerations";

interface BrowserDetails {
  name: string;
  version: number;
  versionLabel: string;
}

interface BrowserCompatibilityWarningProps {
  shouldDisplay: boolean;
  storageKey: string;
  persistOnRefresh?: boolean;
}

const getBrowserWarningStorageKey = (storageKey: string): string =>
  `${CPSConstants.BROWSER_WARNING_STORAGE_PREFIX}${storageKey}`;

export const isBrowserWarningDismissed = (storageKey: string): boolean =>
  sessionStorage.getItem(getBrowserWarningStorageKey(storageKey)) === "true";

export const setBrowserWarningDismissed = (storageKey: string): void => {
  sessionStorage.setItem(getBrowserWarningStorageKey(storageKey), "true");
};

const normalizeDownloadUrl = (url?: string): string => {
  if (!url) {
    return "";
  }

  const cleanedUrl = url.trim().replace(/^['\"]|['\"]$/g, "");

  if (!cleanedUrl) {
    return "";
  }

  if (/^https?:\/\//i.test(cleanedUrl)) {
    return cleanedUrl;
  }

  return `https://${cleanedUrl}`;
};

const detectBrowser = (): BrowserDetails => {
  const ua = (navigator.userAgent || "").toLowerCase();

  if (/trident\/|msie/.test(ua)) {
    const ieMatch = ua.match(/(msie\s|rv:)([\d.]+)/);
    const versionLabel = ieMatch ? ieMatch[2] : "0";
    return {
      name: "Internet Explorer",
      version: parseInt(versionLabel.split(".")[0], 10) || 0,
      versionLabel,
    };
  }

  if (/edg\//.test(ua)) {
    const edgeMatch = ua.match(/edg\/([\d.]+)/);
    const versionLabel = edgeMatch ? edgeMatch[1] : "0";
    return {
      name: "Edge",
      version: parseInt(versionLabel.split(".")[0], 10) || 0,
      versionLabel,
    };
  }

  if (/chrome\//.test(ua) && !/edg\//.test(ua) && !/opr\//.test(ua)) {
    const chromeMatch = ua.match(/chrome\/([\d.]+)/);
    const versionLabel = chromeMatch ? chromeMatch[1] : "0";
    return {
      name: "Chrome",
      version: parseInt(versionLabel.split(".")[0], 10) || 0,
      versionLabel,
    };
  }

  if (/firefox\//.test(ua)) {
    const firefoxMatch = ua.match(/firefox\/([\d.]+)/);
    const versionLabel = firefoxMatch ? firefoxMatch[1] : "0";
    return {
      name: "Firefox",
      version: parseInt(versionLabel.split(".")[0], 10) || 0,
      versionLabel,
    };
  }

  if (/safari\//.test(ua) && !/chrome\//.test(ua) && !/edg\//.test(ua)) {
    const safariMatch = ua.match(/version\/([\d.]+)/);
    const versionLabel = safariMatch ? safariMatch[1] : "0";
    return {
      name: "Safari",
      version: parseInt(versionLabel.split(".")[0], 10) || 0,
      versionLabel,
    };
  }

  return {name: "Unknown", version: 0, versionLabel: "0"};
};

const parseMinVersions = (
  settingsValue?: string | Record<string, number>,
): Record<string, number> => {
  const emptyResult: Record<string, number> = {};

  if (!settingsValue) {
    return emptyResult;
  }

  if (typeof settingsValue === "object") {
    const normalized: Record<string, number> = {};

    Object.keys(settingsValue).forEach(key => {
      const parsedVersion = Number(settingsValue[key]);
      if (!Number.isNaN(parsedVersion) && parsedVersion > 0) {
        normalized[key] = parsedVersion;
      }
    });

    return normalized;
  }

  try {
    const parsed = JSON.parse(settingsValue) as Record<string, number | string>;
    const normalized: Record<string, number> = {};

    Object.keys(parsed).forEach(key => {
      const parsedVersion = Number(parsed[key]);
      if (!Number.isNaN(parsedVersion) && parsedVersion > 0) {
        normalized[key] = parsedVersion;
      }
    });

    return normalized;
  } catch {
    return emptyResult;
  }
};

const getMinimumVersionForBrowser = (
  browserName: string,
  minimumVersionByBrowser: Record<string, number>,
): number | undefined => {
  const normalizedName = normalizeBrowserName(browserName);

  if (typeof minimumVersionByBrowser[normalizedName] === "number") {
    return minimumVersionByBrowser[normalizedName];
  }

  const matchedKey = Object.keys(minimumVersionByBrowser).find(
    key => normalizeBrowserName(key) === normalizedName,
  );

  if (!matchedKey) {
    return undefined;
  }

  return minimumVersionByBrowser[matchedKey];
};

export const isBrowserVersionSupported = (
  browserName: string,
  browserVersion: number,
  minimumVersionByBrowser: Record<string, number>,
): boolean => {
  const minimumVersion = getMinimumVersionForBrowser(
    browserName,
    minimumVersionByBrowser,
  );

  if (typeof minimumVersion !== "number") {
    return true;
  }

  return browserVersion >= minimumVersion;
};

const parseSupportedBrowsers = (settingsValue?: string): string[] => {
  const fallback = process.env.REACT_APP_SUPPORTED_BROWSER_NAMES;
  const source = settingsValue ?? fallback ?? "Chrome,Firefox,Safari,Edge";

  return source
    .split(",")
    .map(browser => browser.trim())
    .filter(browser => browser.length > 0);
};

const normalizeBrowserName = (name: string): string => {
  const normalized = name.trim().toLowerCase();

  if (normalized === "ie" || normalized === "internet explorer") {
    return "Internet Explorer";
  }

  if (normalized === "windows edge" || normalized === "microsoft edge") {
    return "Edge";
  }

  if (normalized === "firefox") {
    return "Firefox";
  }

  if (normalized === "chrome") {
    return "Chrome";
  }

  if (normalized === "safari") {
    return "Safari";
  }

  return name;
};

const getBrowserDisplayName = (name: string): string => {
  if (name === "Chrome") {
    return "Google Chrome";
  }
  if (name === "Edge") {
    return "Microsoft Edge";
  }
  return name;
};

const buildSupportedBrowserText = (
  supportedBrowsers: string[],
  minVersionByBrowser: Record<string, number>,
): string => {
  const parts = supportedBrowsers.map(browserName => {
    const minVersion = getMinimumVersionForBrowser(browserName, minVersionByBrowser);
    const displayName = getBrowserDisplayName(browserName);
    return typeof minVersion === "number"
      ? `${displayName} ${minVersion} and higher`
      : displayName;
  });

  if (parts.length === 0) {
    return "a supported browser";
  }
  if (parts.length === 1) {
    return parts[0];
  }
  if (parts.length === 2) {
    return `${parts[0]} or ${parts[1]}`;
  }
  return `${parts.slice(0, -1).join(", ")}, or ${parts[parts.length - 1]}`;
};

const BrowserCompatibilityWarningDialog: React.FC<BrowserCompatibilityWarningProps> = ({
  shouldDisplay,
  storageKey,
  persistOnRefresh = true,
}) => {
  const [showBrowserDialog, setShowBrowserDialog] = React.useState(false);
  const [browserDialogMessage, setBrowserDialogMessage] = React.useState("");
  const [browserDialogTitle, setBrowserDialogTitle] = React.useState<string>();
  const {
    sendBrowserCompatibilityWarningData: {
      BrowserUpgradeRecommendedTitle,
      EdgeDownloadUrl,
      SafariDownloadUrl,
      DefaultBrowserVersions,
    },
  } = useAppSelector(state => {
    return state.auth;
  });

  const sessionStorageKey = getBrowserWarningStorageKey(storageKey);
  const browserWarningTitle = BrowserUpgradeRecommendedTitle?.trim() || "";
  const browserWarningEdgeDownloadUrl = normalizeDownloadUrl(EdgeDownloadUrl);
  const browserWarningSafariDownloadUrl = normalizeDownloadUrl(SafariDownloadUrl);

  const hasBeenShown = () => isBrowserWarningDismissed(storageKey);

  const markAsShown = () => {
    setBrowserWarningDismissed(storageKey);
  };

  React.useEffect(() => {
    if (!shouldDisplay) {
      setShowBrowserDialog(false);
      return;
    }

    if (hasBeenShown()) {
      return;
    }

    const isWarningEnabledFromEnv =
      process.env.REACT_APP_SHOW_BROWSER_COMPATIBILITY_WARNING !== "false";

    if (!isWarningEnabledFromEnv) {
      setShowBrowserDialog(false);
      markAsShown();
      return;
    }

    const browser = detectBrowser();
    const normalizedName = normalizeBrowserName(browser.name);
    const minVersionByBrowser = parseMinVersions(DefaultBrowserVersions);
    const supportedBrowsers = Object.keys(minVersionByBrowser).length > 0
      ? Object.keys(minVersionByBrowser)
      : parseSupportedBrowsers(undefined);

    const normalizedSupportedBrowsers = supportedBrowsers.map(browserName =>
      normalizeBrowserName(browserName),
    );

    const isBrowserSupported = normalizedSupportedBrowsers.includes(normalizedName);
    const isVersionSupported = isBrowserVersionSupported(
      normalizedName,
      browser.version,
      minVersionByBrowser,
    );

    if (!isBrowserSupported || !isVersionSupported) {
      const supportedBrowserText = buildSupportedBrowserText(
        normalizedSupportedBrowsers,
        minVersionByBrowser,
      );
      const detectedBrowserText = `${getBrowserDisplayName(browser.name)} ${browser.versionLabel}`;
      const generatedMessage =
        `${detectedBrowserText} is not currently a supported Internet Browser. ` +
        `This site is best viewed in ${supportedBrowserText}. ` +
        `Your browser is not supported which may result in compatibility issues. ` +
        "Please utilize a supported browser.";

      setBrowserDialogTitle(browserWarningTitle);
      setBrowserDialogMessage(generatedMessage);
      setShowBrowserDialog(true);
      return;
    }

    markAsShown();
    setShowBrowserDialog(false);
  }, [
    BrowserUpgradeRecommendedTitle,
    DefaultBrowserVersions,
    EdgeDownloadUrl,
    SafariDownloadUrl,
    browserWarningTitle,
    persistOnRefresh,
    sessionStorageKey,
    shouldDisplay,
  ]);

  const handleClose = () => {
    markAsShown();
    setShowBrowserDialog(false);
  };

  const handleBrowserDownloadClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    url: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!shouldDisplay) {
    return null;
  }

  return (
    <Dialog
      open={showBrowserDialog}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      disableEscapeKeyDown
      aria-labelledby="browser-compatibility-title"
      aria-describedby="browser-compatibility-description">
      <DialogCloseCommon onClick={handleClose} />
      <DialogTitleCommon title={browserDialogTitle} />
      <DialogContent id="browser-compatibility-description">
        <Box
          className="wrpGreyBox contactUs"
          sx={{minHeight: "220px", pt: 1, pb: 3, display: "flex", flexDirection: "column", justifyContent: "space-between"}}>
          <Box component="span" id="messageContent">
            <br />
            <Typography component="label">{browserDialogMessage}</Typography>
          </Box>

          <Box component="table" sx={{width: "100%", mt: 2, mb: 1}}>
            <Box component="tbody">
              <Box component="tr">
                <Box component="td" sx={{width: "25%", textAlign: "center"}}>
                  <Link
                    component="a"
                    href={browserWarningEdgeDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={event =>
                      handleBrowserDownloadClick(event, browserWarningEdgeDownloadUrl)
                    }>
                    <img
                      src={EdgeIcon}
                      alt="Microsoft Edge"
                      style={{width: "60px", height: "60px"}}
                    />
                  </Link>
                </Box>
                <Box component="td" sx={{width: "25%", textAlign: "center"}}>
                  <Link
                    component="a"
                    href={browserWarningSafariDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={event =>
                      handleBrowserDownloadClick(event, browserWarningSafariDownloadUrl)
                    }>
                    <img
                      src={SafariIcon}
                      alt="Safari"
                      style={{width: "60px", height: "60px"}}
                    />
                  </Link>
                </Box>
              </Box>
              <Box component="tr">
                <Box component="td" sx={{width: "25%", textAlign: "center"}}>
                  <Link
                    component="a"
                    href={browserWarningEdgeDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={event =>
                      handleBrowserDownloadClick(event, browserWarningEdgeDownloadUrl)
                    }>
                    Microsoft Edge
                  </Link>
                </Box>
                <Box component="td" sx={{width: "25%", textAlign: "center"}}>
                  <Link
                    component="a"
                    href={browserWarningSafariDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={event =>
                      handleBrowserDownloadClick(event, browserWarningSafariDownloadUrl)
                    }>
                    Safari
                  </Link>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export const resetBrowserCompatibilityWarningSession = () => {
  Object.keys(sessionStorage)
    .filter(key => key.startsWith(CPSConstants.BROWSER_WARNING_STORAGE_PREFIX))
    .forEach(key => {
      sessionStorage.removeItem(key);
    });
};

export default BrowserCompatibilityWarningDialog;